import { Navigate, NavLink } from "react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Documentation } from "@/services/backend/documentations/documentation";
import {
  DataTable,
  DataTableContainer,
  useDataTableController,
} from "@/shared/components/data-table";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { formatDate, formatTime } from "@/shared/utilities/date";
import { ReactElement } from "react";
import { Button } from "@/shared/components/ui/button";
import { Edit } from "lucide-react";
import { Username } from "@/shared/components/domain/username/username";
import { useListDifferentiationsQuery } from "@/services/backend/differentiation/service";
import { FilterMode } from "@/shared/lib/filter/filter";

const tableColumns: ColumnDef<Documentation>[] = [
  {
    id: "immutableReason",
    accessorKey: "immutableReason",
    header: "Festgeschrieben weil",
    cell: ({ row }) => {
      const documentationData = row.original;
      return documentationData.immutable ? (
        <p>{documentationData.immutabilityReason as string}</p>
      ) : (
        "Nicht festgeschrieben"
      );
    },
  },
  {
    id: "immutableSetBy",
    accessorKey: "immutableSetBy",
    header: "Festgeschrieben durch",
    cell: ({ row }) => {
      const documenationData = row.original;
      return documenationData.immutable ? (
        <Username
          userId={documenationData.immutabilitySetBy as string}
          className="items-start"
        />
      ) : (
        "Nicht festgeschrieben"
      );
    },
  },
  {
    id: "creationDate",
    accessorKey: "creationDate",
    header: "Dokumentationsstart",
    cell: ({ row }) => {
      const deliveryData = row.original;
      return (
        <div className="items-start">
          <div className="flex flex-col items-start">
            <span className="font-bold">
              {deliveryData.createdAt
                ? formatDate(deliveryData.createdAt!)
                : "Kein Zeiteintrag"}
            </span>
            <span>
              {deliveryData.createdAt
                ? formatTime(deliveryData.createdAt!)
                : "Kein Zeiteintrag"}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    id: "id",
    accessorKey: "id",
    header: "Details",
    cell: ({ row }) => {
      const documentationData = row.original;
      return (
        <NavLink to={documentationData.id}>
          <Button size="sm" className="-my-2">
            <Edit className="h-4 w-4" />
          </Button>
        </NavLink>
      );
    },
  },
];

export interface DifferentiationsTableProps {
  processId: string;
  ownerId?: string;
  ownerType?: string;
}

export function DifferentiationsTable({
  processId,
  ownerId,
  ownerType,
}: DifferentiationsTableProps): ReactElement {
  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useListDifferentiationsQuery(
    {
      processId: {
        active: true,
        values: [processId as string],
      },
      ownerType: {
        active: true,
        mode: ownerType ? FilterMode.Like : FilterMode.IsNull,
        values: [ownerType || ""],
      },
      ownerId: {
        active: true,
        mode: ownerId ? FilterMode.Like : FilterMode.IsNull,
        values: [ownerId || ""],
      },
      limit: 0,
      offset: 0,
    },
    { refetchOnMountOrArgChange: true },
  );

  const table = useDataTableController(tableColumns, list?.items || []);

  if (isLoading || isFetching) {
    return <div />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (list != null && list.items != null && list?.count !== 0) {
    return <Navigate to={`${list.items[0].id}`} />;
  }

  return (
    <DataTableContainer>
      <DataTable table={table} columns={tableColumns} />
    </DataTableContainer>
  );
}
